import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { visiteAPI, type GetVisiteParams, type GetCalendarParams } from '@offertify/api-client';
import type { VisitaCreate, VisitaUpdate } from '@offertify/core';
import { VISITE_OFFLINE_MAX_AGE } from '../offline/constants';

/**
 * Query keys for the Visite domain. Every key starts with 'visite' (or 'visita'
 * for a single record) so a mutation can invalidate all lists at once with the
 * `all`/`lists` prefix, and so the offline persister can select the whole domain
 * by its root key.
 */
export const visitaKeys = {
  all: ['visite'] as const,
  lists: () => ['visite'] as const,
  list: (params?: GetVisiteParams) => ['visite', params] as const,
  byCliente: (clienteId: string | undefined, params?: Omit<GetVisiteParams, 'cliente_id'>) =>
    ['visite', 'cliente', clienteId, params] as const,
  byOfferta: (offertaId: string | undefined, params?: Omit<GetVisiteParams, 'offerta_id'>) =>
    ['visite', 'offerta', offertaId, params] as const,
  calendar: (params: GetCalendarParams) => ['visite', 'calendar', params] as const,
  detail: (id: string | undefined) => ['visita', id] as const,
};

/** Paginated visits list with optional date/stato/cliente filters. */
export function useVisite(params?: GetVisiteParams) {
  return useQuery({
    queryKey: visitaKeys.list(params),
    queryFn: () => visiteAPI.getVisite(params).then((r) => r.data),
    gcTime: VISITE_OFFLINE_MAX_AGE,
  });
}

/**
 * Every activity within a date window (calendar view). Unpaginated: the caller
 * passes the visible month/grid range and buckets the result by day. Disabled
 * until both bounds are set so we never fetch an unbounded window.
 */
export function useVisiteCalendar(params: GetCalendarParams, enabled = true) {
  return useQuery({
    queryKey: visitaKeys.calendar(params),
    queryFn: () => visiteAPI.getCalendar(params).then((r) => r.data),
    enabled: enabled && !!params.from_date && !!params.to_date,
    gcTime: VISITE_OFFLINE_MAX_AGE,
  });
}

/** Single visit by id. Disabled until an id is available. */
export function useVisita(id: string | undefined) {
  return useQuery({
    queryKey: visitaKeys.detail(id),
    queryFn: () => visiteAPI.getVisita(id!).then((r) => r.data),
    enabled: !!id,
    gcTime: VISITE_OFFLINE_MAX_AGE,
  });
}

/**
 * A client's visits (agenda della scheda cliente). Convenience over useVisite
 * that pins cliente_id; disabled until a client id is available.
 */
export function useVisiteByCliente(
  clienteId: string | undefined,
  params?: Omit<GetVisiteParams, 'cliente_id'>,
) {
  return useQuery({
    queryKey: visitaKeys.byCliente(clienteId, params),
    queryFn: () => visiteAPI.getVisite({ ...params, cliente_id: clienteId! }).then((r) => r.data),
    enabled: !!clienteId,
    gcTime: VISITE_OFFLINE_MAX_AGE,
  });
}

/**
 * An offer's activities (tab Attività su OfferEditor). Convenience over useVisite
 * that pins offerta_id; disabled until an offerta id is available.
 */
export function useVisiteByOfferta(
  offertaId: string | undefined,
  params?: Omit<GetVisiteParams, 'offerta_id'>,
) {
  return useQuery({
    queryKey: visitaKeys.byOfferta(offertaId, params),
    queryFn: () => visiteAPI.getVisite({ ...params, offerta_id: offertaId! }).then((r) => r.data),
    enabled: !!offertaId,
    gcTime: VISITE_OFFLINE_MAX_AGE,
  });
}

/** Create a visit and refresh every visits list. UI side effects are the caller's. */
export function useCreateVisita() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: VisitaCreate) => visiteAPI.createVisita(data).then((r) => r.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: visitaKeys.lists() });
      queryClient.invalidateQueries({ queryKey: ['activity-analytics'] });
      queryClient.invalidateQueries({ queryKey: ['inactive-clients'] });
    },
  });
}

/** Update a visit (incl. stato/esito/follow-up) and refresh its lists + detail. */
export function useUpdateVisita() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: VisitaUpdate }) =>
      visiteAPI.updateVisita(id, data).then((r) => r.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: visitaKeys.lists() });
      queryClient.invalidateQueries({ queryKey: ['activity-analytics'] });
      queryClient.invalidateQueries({ queryKey: ['inactive-clients'] });
      queryClient.invalidateQueries({ queryKey: ['visita'] });
    },
  });
}

/** Soft-delete a visit (backend marks is_deleted) and refresh its lists + detail. */
export function useDeleteVisita() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (target: string | { id: string; scope: 'singola' | 'serie' }) =>
      visiteAPI.deleteVisita(typeof target === 'string' ? target : target.id, typeof target === 'string' ? 'singola' : target.scope).then((r) => r.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: visitaKeys.lists() });
      queryClient.invalidateQueries({ queryKey: ['activity-analytics'] });
      queryClient.invalidateQueries({ queryKey: ['inactive-clients'] });
      queryClient.invalidateQueries({ queryKey: ['visita'] });
    },
  });
}

/**
 * Attachments of a visit (issue #105). Own root key on purpose: the payload holds
 * presigned URLs that expire after 1h, so it must stay out of the offline
 * 'visite'/'visita' persister and be refetched instead. `staleTime` keeps the
 * previews valid well within that TTL.
 */
export const visitaAllegatiKeys = {
  list: (visitaId: string | undefined) => ['visita-allegati', visitaId] as const,
};

export function useVisitaAllegati(visitaId: string | undefined) {
  return useQuery({
    queryKey: visitaAllegatiKeys.list(visitaId),
    queryFn: () => visiteAPI.getAllegati(visitaId!).then((r) => r.data),
    enabled: !!visitaId,
    staleTime: 30 * 60 * 1000,
  });
}

export function useUploadVisitaAllegato(visitaId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      file,
      descrizione,
      onProgress,
    }: {
      file: Blob | { uri: string; name: string; type: string };
      descrizione?: string;
      onProgress?: (percent: number) => void;
    }) => visiteAPI.uploadAllegato(visitaId, file, descrizione, onProgress).then((r) => r.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: visitaAllegatiKeys.list(visitaId) });
    },
  });
}

export function useDeleteVisitaAllegato(visitaId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (allegatoId: string) =>
      visiteAPI.deleteAllegato(visitaId, allegatoId).then((r) => r.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: visitaAllegatiKeys.list(visitaId) });
    },
  });
}
